import type { Locale } from 'date-fns';
import {
  endOfDay,
  format,
  isAfter,
  isValid,
  parse,
  startOfDay,
  eachDayOfInterval,
  startOfISOWeek,
  endOfISOWeek
} from 'date-fns';
import { de, fr, it } from 'date-fns/locale';

export const minReportDate = startOfDay(new Date(new Date().setFullYear(new Date().getFullYear() - 5)));
export const maxReportDate = endOfDay(new Date());

export const getLocale = (language: string): Locale => {
  switch (language) {
    case 'de':
      return de;
    case 'fr':
      return fr;
    case 'it':
      return it;
    default:
      return de;
  }
};

export const formatDateByLanguage = (date: Date, language: string, dateFormat?: string) => {
  return formatDate(date, dateFormat || 'dd.MM.yyyy', getLocale(language));
};

export const formatDate = (date: Date, dateFormat?: string, locale?: Locale): string | undefined => {
  if (isValid(date)) {
    return format(date, dateFormat || 'dd.MM.yyyy', {
      locale: locale
    });
  }
  return undefined;
};

const DATE_LITERAL_RE = /^\d{2}\.\d{2}\.\d{4}$/;

export function isCompleteDateLiteral(value: string): boolean {
  return DATE_LITERAL_RE.test(value);
}

export function parseDate(value: string, format = 'dd.MM.yyyy', returnDefaultDateWhenEmpty = true): Date {
  if (value) {
    const parsedDate = parse(value, format, new Date());
    if (isValid(parsedDate)) {
      return parsedDate;
    }
    throw new Error('Invalid date');
  }
  if (returnDefaultDateWhenEmpty) {
    return new Date();
  }
  throw new Error('Invalid date');
}

export function normalizeDate(date: Date | null | undefined): string {
  if (!date || !isValid(date)) {
    return '';
  }
  const formattedDate = formatDate(endOfDay(date));
  return formattedDate ?? '';
}

export function isAfterDay(date: Date, dateToCompare: Date): boolean {
  const dateStart = startOfDay(date);
  const dateToCompareStart = startOfDay(dateToCompare);
  return isAfter(dateStart, dateToCompareStart);
}

export const weekdays = (language: string): string[] => {
  const locale = getLocale(language);

  const start = startOfISOWeek(new Date());
  const end = endOfISOWeek(new Date());

  const arr = eachDayOfInterval({ start: start, end: end });

  const weekdays = arr.map(a => formatDate(a, 'EEEEEE', locale)).filter<string>((d): d is string => d !== undefined);

  return weekdays;
};
